import React from 'react';
import { Phone, PhoneOff, Mic, MicOff, Volume2, VolumeX, Circle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { formatDuration } from './formatDuration';

export default function SoftPhoneActiveCall({
  view, duration, selectedContact, currentCall,
  isMuted, setIsMuted, isSpeakerOff, setIsSpeakerOff,
  isRecording, isStaff, onToggleRecording, onEndCall
}) {
  const contactName = selectedContact?.full_name || currentCall?.callee_name || currentCall?.caller_name || '?';

  return (
    <div data-testid="softphone-active-call" className="px-6 py-8 text-center bg-gradient-to-b from-slate-800 to-slate-900">
      {view === 'active' && (
        <p className="text-xs font-semibold text-green-400 tracking-widest uppercase mb-4">Appel en cours</p>
      )}
      {view === 'ringing' && (
        <p className="text-xs font-semibold text-amber-400 tracking-widest uppercase mb-4 animate-pulse">Appel en cours...</p>
      )}
      <div className="w-20 h-20 rounded-full bg-slate-700/60 mx-auto mb-4 flex items-center justify-center ring-4 ring-slate-600/30">
        <span className="text-3xl font-bold text-white">{contactName.charAt(0)}</span>
      </div>
      <p className="text-white font-bold text-xl mb-1">{contactName}</p>
      <p className="text-3xl font-mono text-white tracking-wider mb-8">{formatDuration(duration)}</p>
      <div className="flex justify-center gap-5">
        <button data-testid="softphone-mute-btn" onClick={() => setIsMuted(!isMuted)} className="flex flex-col items-center gap-1.5">
          <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${isMuted ? 'bg-red-500 shadow-red-500/30 shadow-lg' : 'bg-slate-700 hover:bg-slate-600'}`}>
            {isMuted ? <MicOff className="w-6 h-6 text-white" /> : <Mic className="w-6 h-6 text-white" />}
          </div>
          <span className="text-[10px] text-slate-400">{isMuted ? 'Reactiver' : 'Muet'}</span>
        </button>
        <button data-testid="softphone-speaker-btn" onClick={() => setIsSpeakerOff(!isSpeakerOff)} className="flex flex-col items-center gap-1.5">
          <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${isSpeakerOff ? 'bg-red-500 shadow-red-500/30 shadow-lg' : 'bg-slate-700 hover:bg-slate-600'}`}>
            {isSpeakerOff ? <VolumeX className="w-6 h-6 text-white" /> : <Volume2 className="w-6 h-6 text-white" />}
          </div>
          <span className="text-[10px] text-slate-400">Haut-parleur</span>
        </button>
        {isStaff && view === 'active' && (
          <button data-testid="softphone-record-btn" onClick={onToggleRecording} className="flex flex-col items-center gap-1.5">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${isRecording ? 'bg-red-600 shadow-red-600/40 shadow-lg animate-pulse' : 'bg-slate-700 hover:bg-slate-600'}`}>
              <Circle className={`w-6 h-6 ${isRecording ? 'fill-white text-white' : 'text-white'}`} />
            </div>
            <span className="text-[10px] text-slate-400">{isRecording ? 'Arreter' : 'Enregistrer'}</span>
          </button>
        )}
        <button data-testid="softphone-hangup-btn" onClick={onEndCall} className="flex flex-col items-center gap-1.5">
          <div className="w-14 h-14 rounded-full bg-red-600 hover:bg-red-700 flex items-center justify-center transition-all shadow-red-600/30 shadow-lg">
            <PhoneOff className="w-6 h-6 text-white" />
          </div>
          <span className="text-[10px] text-slate-400">Fin</span>
        </button>
      </div>
    </div>
  );
}

export function SoftPhoneIncoming({ currentCall, onAnswer, onDecline }) {
  return (
    <div data-testid="softphone-incoming-call" className="px-6 py-8 text-center bg-gradient-to-b from-slate-800 to-slate-900">
      <p className="text-xs font-semibold text-green-400 tracking-widest uppercase mb-4 animate-pulse">Appel entrant</p>
      <div className="w-20 h-20 rounded-full bg-green-900/40 mx-auto mb-4 flex items-center justify-center ring-4 ring-green-500/30 animate-pulse">
        <span className="text-3xl font-bold text-green-300">{(currentCall?.caller_name || '?').charAt(0)}</span>
      </div>
      <p className="text-white font-bold text-xl mb-1">{currentCall?.caller_name || 'Appel entrant'}</p>
      <p className="text-slate-400 text-sm mb-8">Appel entrant...</p>
      <div className="flex justify-center gap-8">
        <button data-testid="softphone-decline-btn" onClick={onDecline} className="flex flex-col items-center gap-1.5">
          <div className="w-16 h-16 rounded-full bg-red-600 hover:bg-red-700 flex items-center justify-center transition-all shadow-red-600/30 shadow-lg">
            <PhoneOff className="w-7 h-7 text-white" />
          </div>
          <span className="text-[10px] text-slate-400">Refuser</span>
        </button>
        <button data-testid="softphone-answer-btn" onClick={onAnswer} className="flex flex-col items-center gap-1.5">
          <div className="w-16 h-16 rounded-full bg-green-600 hover:bg-green-700 flex items-center justify-center transition-all shadow-green-600/30 shadow-lg animate-bounce">
            <Phone className="w-7 h-7 text-white" />
          </div>
          <span className="text-[10px] text-slate-400">Repondre</span>
        </button>
      </div>
    </div>
  );
}
